/** Standard pagination metadata returned by list APIs (`page` + `limit` query params). */
export interface PaginationMeta {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface PaginationQuery {
  page?: number;
  limit?: number;
  /** Backward-compatible alias; converted to page on the backend when `page` is omitted. */
  offset?: number;
}

export const DEFAULT_PAGE_SIZE = 20;
export const DEFAULT_PAGE = 1;
/** Page size for catalog dropdowns that must load every row (API max is 200). */
export const CATALOG_PAGE_SIZE = 200;

export type PaginatedResponse<T extends Record<string, unknown>> = T & PaginationMeta;

export function emptyPagination(page = DEFAULT_PAGE, perPage = DEFAULT_PAGE_SIZE): PaginationMeta {
  return { total: 0, page, per_page: perPage, total_pages: 0 };
}

export function readPagination(data: Partial<PaginationMeta> | null | undefined, fallbackPage = 1, fallbackSize = DEFAULT_PAGE_SIZE): PaginationMeta {
  if (!data) return emptyPagination(fallbackPage, fallbackSize);
  const perPage = data.per_page ?? fallbackSize;
  const page = data.page ?? fallbackPage;
  const total = data.total ?? 0;
  const totalPages = data.total_pages ?? (perPage > 0 ? Math.ceil(total / perPage) : 0);
  return { total, page, per_page: perPage, total_pages: totalPages };
}

export function appendPaginationQuery(qs: URLSearchParams, params?: PaginationQuery): void {
  if (!params) return;
  if (params.page != null) qs.append('page', String(params.page));
  if (params.limit != null) qs.append('limit', String(params.limit));
  if (params.offset != null && params.page == null) qs.append('offset', String(params.offset));
}

/** Build pagination metadata for client-side sliced lists. */
export function buildLocalPaginationMeta(
  total: number,
  page = DEFAULT_PAGE,
  pageSize = DEFAULT_PAGE_SIZE
): PaginationMeta {
  const perPage = Math.max(1, pageSize);
  const totalPages = total > 0 ? Math.ceil(total / perPage) : 0;
  const safePage = totalPages > 0 ? Math.min(Math.max(page, 1), totalPages) : DEFAULT_PAGE;
  return { total, page: safePage, per_page: perPage, total_pages: totalPages };
}

/** Slice an array for the current page (1-based page index). */
export function paginateLocalItems<T>(items: T[], page: number, pageSize: number): T[] {
  const meta = buildLocalPaginationMeta(items.length, page, pageSize);
  const start = (meta.page - 1) * meta.per_page;
  return items.slice(start, start + meta.per_page);
}

/** Walk server-paginated list endpoints until every page is fetched. */
export async function collectAllPages<T>(
  fetchPage: (
    page: number,
    limit: number
  ) => Promise<{ items: T[]; totalPages?: number | null }>
): Promise<T[]> {
  const all: T[] = [];
  let page = 1;
  let totalPages = 1;
  do {
    const { items, totalPages: pages } = await fetchPage(page, CATALOG_PAGE_SIZE);
    all.push(...items);
    totalPages = Math.max(1, Number(pages ?? 1));
    page += 1;
  } while (page <= totalPages);
  return all;
}

/** True when the API response includes server-side pagination metadata. */
export function hasServerPagination(data: Partial<PaginationMeta> | null | undefined): boolean {
  return typeof data?.total === 'number';
}

/** Use server-paginated rows when available; otherwise slice locally. */
export function resolvePaginatedList<T>(
  items: T[],
  data: Partial<PaginationMeta> | null | undefined,
  page: number,
  pageSize: number
): { items: T[]; meta: PaginationMeta } {
  if (hasServerPagination(data)) {
    return { items, meta: readPagination(data, page, pageSize) };
  }
  const meta = buildLocalPaginationMeta(items.length, page, pageSize);
  return { items: paginateLocalItems(items, page, pageSize), meta };
}
